import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import { supabase } from "../utils/supabaseClient";
import { logger } from "../utils/logger";

const todayYMD = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtRelDate = (ymd) => {
  if (!ymd) return "";
  try {
    return new Date(ymd).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return ymd;
  }
};

const SavedSearchesDialog = ({ open, onClose, isPro }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setItems([]);
        setError("Sign in to see your saved searches.");
        return;
      }
      const { data, error: qErr } = await supabase
        .from("saved_searches")
        .select("id, label, search_params, departure_date, created_at")
        .gte("departure_date", todayYMD())
        .order("created_at", { ascending: false });
      if (qErr) {
        logger.error?.("saved_searches fetch error", qErr);
        setError("Couldn't load saved searches.");
        setItems([]);
        return;
      }
      setItems(data || []);
    } catch (err) {
      logger.error?.("saved_searches fetch threw", err);
      setError("Couldn't load saved searches.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  const handleOpen = (item) => {
    window.dispatchEvent(
      new CustomEvent("load-saved-search", { detail: item.search_params })
    );
  };

  const handleDelete = async (item) => {
    setBusyId(item.id);
    try {
      const { error: dErr } = await supabase
        .from("saved_searches")
        .delete()
        .eq("id", item.id);
      if (dErr) {
        logger.error?.("saved_searches delete error", dErr);
        return;
      }
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      // Notify Home so the bookmark icon on the current search updates
      window.dispatchEvent(new CustomEvent("saved-searches-changed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          backgroundImage:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #1e1e1e, #121212)"
              : "linear-gradient(145deg, #ffffff, #f5f5f7)",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 5 }}>
        <BookmarkRoundedIcon sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Saved searches
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ opacity: 0.4 }} />
      <DialogContent sx={{ pt: 2, pb: 2 }}>
        {!isPro ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 700 }}>
              Saving searches is a Pro perk
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upgrade to save searches under your profile and re-run them in one tap.
            </Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            {error}
          </Typography>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 700 }}>
              No saved searches yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Run a search, then tap the bookmark icon to save it here.
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {items.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  "&:hover": { bgcolor: "action.hover" },
                }}
                secondaryAction={
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <IconButton
                      edge="end"
                      onClick={() => handleOpen(item)}
                      aria-label="open"
                      title="Open this search"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDelete(item)}
                      disabled={busyId === item.id}
                      aria-label="delete"
                      title="Delete"
                    >
                      {busyId === item.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <DeleteOutlineIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ fontWeight: 700, pr: 8 }}>
                      {item.label}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      Departs {fmtRelDate(item.departure_date)}
                    </Typography>
                  }
                  onClick={() => handleOpen(item)}
                  sx={{ cursor: "pointer" }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <Box sx={{ px: 3, pb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "text.secondary" }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default SavedSearchesDialog;
